/* oxlint-disable react/rules-of-hooks -- Existing hook usage is preserved during the linter migration. */
/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import * as React from 'react';
import { usePress } from 'react-aria';

import { Logger } from '@gitbook/util-debug';

const logger = Logger('ListItem');

export interface ListItemProps extends React.HTMLAttributes<HTMLLIElement> {
    /** Sets the style of the list item. @default 'default' */
    kind?: 'default' | 'bordered';
    /** Sets the orientation of the list item content. @default 'horizontal'. */
    orientation?: 'horizontal' | 'vertical';
    /** Align the item content. @default 'center'. */
    align?: 'center' | 'start';
    /** Set a subtler background color on hover. @default 'false'. */
    subtle?: boolean;
    /** Various padding options. @default 'medium'. */
    size?: ListItemSize;
    /** Remove horizontal padding if true. @default false. */
    trimX?: boolean;
    /** Remove vertical padding if true. @default false */
    trimY?: boolean;
    /** Show the item as currently active. */
    active?: boolean;
    /** Show the item as currently selected. */
    selected?: boolean;
    /** Callback when pressing the item. If one is passed the component is interactive. */
    onPress?: () => void;
    /** Prevent focusing the item when pressing it. @default false */
    preventFocusOnPress?: boolean;
    /** Content of the item. */
    children: React.ReactNode;
}
const ListItemContext = React.createContext<{
    labelId: string;
    size: ListItemSize;
}>({
    labelId: '',
    size: 'medium',
});
type ListItemSize = 'small' | 'medium' | 'large';

/**
 * A single item in a list.
 *
 * Should be wrapped in a `List` component.
 */
export function ListItem({
    ref,
    ...props
}: ListItemProps & { ref?: React.Ref<HTMLLIElement> }): React.ReactElement {
    const {
        kind = 'default',
        orientation = 'horizontal',
        active = false,
        selected = false,
        trimX = false,
        trimY = false,
        size = 'medium',
        align = 'center',
        className,
        onPress,
        preventFocusOnPress = false,
        children,
        ...rest
    } = props;

    const press = usePress({ onPress, preventFocusOnPress });
    const labelId = React.useId();
    const contextValue = React.useMemo(() => ({ labelId, size }), [labelId, size]);

    if (process.env.NODE_ENV === 'development') {
        React.useLayoutEffect(() => {
            const label = document.getElementById(labelId);
            if (!label) {
                logger.error('ListItem: Missing ListItemDescription');
            }
        }, [labelId]);
    }

    return (
        <ListItemContext.Provider value={contextValue}>
            <li
                ref={ref}
                data-selected={selected || undefined}
                aria-current={active || undefined}
                aria-selected={selected || undefined}
                aria-labelledby={labelId}
                {...(onPress
                    ? {
                          ...press.pressProps,
                          tabIndex: 0,
                      }
                    : {})}
                className={clsx(
                    'group/listitem flex',
                    kindClasses[kind],
                    {
                        'flex-row': orientation === 'horizontal',
                        'flex-col': orientation === 'vertical',
                    },
                    {
                        'items-start': align === 'start',
                        'items-center': align === 'center',
                    },
                    {
                        small: 'gap-2 p-1',
                        medium: 'gap-3 px-3 py-2',
                        large: 'gap-3 px-4 py-3',
                    }[size],
                    trimX && 'pr-0 pl-0',
                    trimY && 'py-0',
                    onPress
                        ? props.subtle
                            ? 'cursor-pointer hover:bg-(--neutral-2) focus-visible:shadow-focus-ring aria-selected:bg-(--neutral-3)'
                            : 'cursor-pointer hover:bg-(--neutral-3) focus-visible:shadow-focus-ring aria-selected:bg-(--neutral-4)'
                        : null,
                    className
                )}
                {...rest}
            >
                {children}
            </li>
        </ListItemContext.Provider>
    );
}

const kindClasses = {
    default: 'bg-inherit rounded-lg',
    // Rounded at the ends, so a hover or selected background follows the curve of the bordered
    // list framing the items instead of squaring off its corners.
    bordered:
        'border-b-[0.5px] first-of-type:rounded-t-lg last-of-type:rounded-b-lg last-of-type:border-b-0',
};

/**
 * Displayed on the left side of the list item.
 * Useful for avatar, icon, badge and such.
 */
export function ListItemLeading(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    return (
        <div className={clsx('flex shrink-0 items-center', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Main content of the list item.
 * Accepts as children:
 * - `ListItemDescription`
 * - `ListItemDetail`
 */
export function ListItemBody(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    const { size } = React.useContext(ListItemContext);
    return (
        <div
            className={clsx(
                'flex flex-1 flex-col',
                size === 'small' ? 'gap-0' : 'gap-1',
                className
            )}
            {...rest}
        >
            {children}
        </div>
    );
}

/**
 * Text description to be displayed as the first line in a list item's body.
 */
export function ListItemDescription(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLParagraphElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    const { labelId } = React.useContext(ListItemContext);
    return (
        <div id={labelId} className={clsx('text-ui-base', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Text title to be displayed below any header or description in a list item's body.
 */
export function ListItemDetail(
    props: { children: React.ReactNode } & React.HTMLAttributes<HTMLParagraphElement>
): React.ReactElement {
    const { children, className, ...rest } = props;
    return (
        <div className={clsx('text-ui-small text-muted', className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Elements to be displayed on the right side of the list item.
 * Suitable for buttons, badges, icons, status indicators and such.
 */
export function ListItemTrailing(
    props: {
        /**
         * When to display the controls, based on the item's state.
         *   - 'always': always show the controls
         *   - 'focused': show the controls when the item is focused/hovered
         * @default 'focused'
         */
        when?: 'always' | 'focused';
        className?: string;
        children: React.ReactNode;
    } & React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
    const { children, className, when = 'focused', ...rest } = props;
    return (
        <div
            className={clsx(
                'flex shrink-0 items-center',
                when === 'always'
                    ? null
                    : 'opacity-0 group-[[aria-selected]]/listitem:opacity-100 has-[:focus-visible]:opacity-100',
                when === 'focused'
                    ? 'group-hover/listitem:opacity-100 group-focus-visible/listitem:opacity-100'
                    : null,
                className
            )}
            {...rest}
        >
            {children}
        </div>
    );
}
